// climate_admin_panel\src\pages\Events\Events.jsx

import { RemoveCircleOutline, RemoveRedEye, Search } from '@mui/icons-material'
import { Button, Tab, Tabs, Dialog, DialogActions, DialogTitle, FormControl, Grid, MenuItem, Pagination, Paper, Select, Table, TableBody, TableCell, TableHead, TableRow, Tooltip, Chip, Backdrop, CircularProgress, Typography, Box, IconButton, ToggleButton, ToggleButtonGroup } from '@mui/material'
import { styled } from '@mui/system'
import React, { useEffect, useState, useCallback, useMemo } from 'react'
import { NavLink, useLocation, useNavigate } from 'react-router-dom'
import { toast, ToastContainer } from 'react-toastify'
import Loader from '../../components/Loader/Loader'
import Toptag from '../../components/toptag/Toptag'
import "./events.css"
import apiFunctions from '../../api/apiFunctions'
import ImportEventDialog from './ImportEventDialog'


const NavPaper = styled(Paper)({
    padding: "5px 12px",
    borderRadius: "0",
    backgroundColor: "white",
    boxShadow: "rgba(0, 0, 0, 0.02) 0px 1px 3px 0px, rgba(27, 31, 35, 0.15) 0px 0px 0px 1px"
})

const BackPaper = styled(Paper)({
    minHeight: "calc(100vh - 130px)",
    overflow: "visible",
    boxShadow: "rgba(0, 0, 0, 0.02) 0px 1px 3px 0px, rgba(27, 31, 35, 0.15) 0px 0px 0px 1px",
    position: 'relative'
})

const BlurredContainer = styled('div')(({ blur }) => ({
    opacity: blur ? 0.6 : 1,
    transition: 'opacity 0.2s ease',
    pointerEvents: blur ? 'none' : 'auto'
}))


const Events = () => {
    const [all, setAll] = useState([]);
    const [isLoading, setIsloading] = useState(true)
    const [eventCount, setEventCount] = useState(0)
    const navigate = useNavigate();
    const [limit, setLimit] = useState(50);
    const [skip, setSkip] = useState(0);
    const [page, setPage] = useState("1")
    const [searchKey, setSearchKey] = useState("")
    const [searchInput, setSearchInput] = useState("")
    const [openDialogForRemove, setOpenDialogForRemove] = React.useState(false);
    const [openDialogForAddEvent, setOpenDialogForAddEvent] = React.useState(false);
    const [id, setId] = useState()
    const [type, setType] = useState("active")
    const [eventTypeFilter, setEventTypeFilter] = useState("all")
    const [changeColour, setChangeColour] = useState("yellow")


    const handleRemoveOpen = (id) => {
        setId(id)
        setOpenDialogForRemove(true);
    };

    const handleAddEventPopOpen = () => {
        setOpenDialogForAddEvent(true);
    };


    const handleRemoveClose = () => {
        setOpenDialogForRemove(false);
    };

    const searchHandle = (event) => {
        setSearchInput(event.target.value);
    }

    const handleSearchClick = () => {
        setSearchKey(searchInput);
        setPage("1");
        setSkip(0);
    }

    const handleSearchKeyPress = (event) => {
        if (event.key === 'Enter') {
            handleSearchClick();
        }
    }

    const getAll = useCallback(async () => {
        setIsloading(true)
        try {
            let result = await apiFunctions.getAllEvents(limit, searchKey, page, type, eventTypeFilter);
            if (!result.data.status) {
                if (result.data.code === 401) {
                    localStorage.clear();
                    navigate("/login")
                    toast.info("Session Expired Please login again!!")
                }
                toast.error(result.data.message, {
                    position: toast.POSITION.TOP_RIGHT
                });
            } else {
                setAll(result.data.data);
                setEventCount(result.data.total_count)
            }
        } catch (error) {
            console.error('Error fetching events:', error);
            toast.error('Failed to fetch events', {
                position: toast.POSITION.TOP_RIGHT
            });
        } finally {
            setIsloading(false)
        }
    }, [searchKey, limit, page, type, eventTypeFilter, navigate])

    useEffect(() => {
        getAll()
    }, [getAll])


    const handleChangeEventTypeFilter = (event, newValue) => {
        if (!newValue) return;
        setEventTypeFilter(newValue);
        setPage("1");
        setSkip(0);
    }

    const handleChangeType = (event, newValue) => {
        setType(newValue);
        setChangeColour("black");
        setPage("1");
        setSkip(0);
    }

    const removeEvent = useCallback(async (id) => {
        const body = {
            _id: id
        }
        const result = await apiFunctions.removeEvent(body);
        if (!result.data.status) {
            if (result.data.code === 401) {
                localStorage.clear();
                navigate("/login")
                toast.info("Session Expired Please login again!!")
            }
            toast.error(result.data.message, {
                position: toast.POSITION.TOP_RIGHT
            });
        } else {
            setOpenDialogForRemove(false);
            getAll();
            toast.success(result.data.message, {
                position: toast.POSITION.TOP_RIGHT
            });
        }
    }, [getAll, navigate])

    const handleChange = (e) => {
        // Select value arrives as a string; compare/store as Number so the "All"
        // option (value={eventCount}) actually matches instead of never triggering.
        const v = Number(e.target.value)
        if (v === eventCount) {
            setSkip(0)
            setLimit(eventCount)
        } else {
            setLimit(v)
        }
    };

    const skipPagination = (e, page) => {
        setSkip((page - 1) * limit)
        setPage(page)
    }

    const truncateString = useCallback((str, maxLength) => {
        if (str == null || str === '') return 'N/A';
        return str.length > maxLength ? str.substring(0, maxLength) : str;
    }, [])

    const [opened, setOpened] = useState(false);

    return (
        <>
            <Toptag />
            <ToastContainer autoClose={1000} />
            <BlurredContainer blur={isLoading}>
                <Grid container spacing={3} direction="row" justifyContent="center" alignItems="center">
                        <Grid item>
                            <Tabs
                                value={type}
                                onChange={handleChangeType}
                                aria-label="event time filter"
                            >
                                <Tab
                                    style={{ background: { changeColour } }}
                                    value="past"
                                    label="PAST"
                                />

                                <Tab
                                    style={{ background: { changeColour } }}
                                    value="active"
                                    label="ACTIVE"
                                />
                            </Tabs>
                        </Grid>
                        <Grid item>
                            <ToggleButtonGroup
                                exclusive
                                size="small"
                                value={eventTypeFilter}
                                onChange={handleChangeEventTypeFilter}
                                aria-label="event type filter"
                            >
                                <ToggleButton value="all">All types</ToggleButton>
                                <ToggleButton value="Special">Special</ToggleButton>
                                <ToggleButton value="Normal">Normal</ToggleButton>
                            </ToggleButtonGroup>
                        </Grid>

                    </Grid>
                    <NavPaper>
                        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                            <div></div>
                            <Grid container spacing={4} direction="row" justifyContent="flex-end" alignItems="center">
                                <Grid item><div style={{ fontSize: "15px", fontWeight: "400", color: "grey" }}>Total Events - {eventCount}</div></Grid>
                                <Grid item><div style={{ fontSize: "15px", fontWeight: "400", color: "grey" }}>Row Per Page - </div></Grid>
                                <Grid item>
                                    <FormControl variant="standard" sx={{ m: 1, minWidth: 50 }}>
                                        <Select
                                            labelId="demo-simple-select-standard-label"
                                            id="demo-simple-select-standard-label"
                                            value={limit}
                                            onChange={handleChange}
                                        >
                                            <MenuItem value="50">50</MenuItem>
                                            <MenuItem value="100">100</MenuItem>
                                            <MenuItem value="150">150</MenuItem>
                                            <MenuItem value="200">200</MenuItem>
                                        </Select>
                                    </FormControl>
                                </Grid>
                                <Grid item>
                                    <div className='searchBar' style={{ display: 'flex', alignItems: 'center' }}>
                                        <input 
                                            type="text" 
                                            placeholder='Search... ' 
                                            value={searchInput}
                                            onChange={searchHandle} 
                                            onKeyPress={handleSearchKeyPress}
                                            id='searchtext' 
                                            className='searchBarInput' 
                                        />
                                        <IconButton 
                                            onClick={handleSearchClick}
                                            sx={{ padding: '4px', color: '#666' }}
                                            aria-label="search"
                                        >
                                            <Search />
                                        </IconButton>
                                    </div>
                                </Grid>
                                <Grid item>
                                    <button className='newBtn' onClick={() => { handleAddEventPopOpen() }}>Add New Event</button>
                                    <button className='newBtn' onClick={() => { setOpened(true) }}>Import Events</button>
                                </Grid>
                            </Grid>


                        </div>
                    </NavPaper>
                    <BackPaper>
                        {all.length === 0 && searchKey ?
                            <Grid container spacing={2} style={{ marginTop: "30px" }} direction="column" justifyContent="space-evenly" alignItems="center">
                                <Grid item><img src="/images/nodata.jpg" alt="error-image" style={{ height: "150px", width: "200px" }} /></Grid>
                                <Grid item><div className="errorMessage">No events found!</div></Grid>
                            </Grid> :
                            <>
                                <Table stickyHeader aria-label="sticky table">
                                    <TableHead>
                                        <TableRow>
                                            <TableCell align="center">S.No</TableCell>
                                            <TableCell align="center">Title</TableCell>
                                            <TableCell align="center">Date</TableCell>
                                            <TableCell align="center">place</TableCell>
                                            <TableCell align="center">Country</TableCell>
                                            <TableCell align="center">Type</TableCell>
                                            <TableCell align="center">Status</TableCell>
                                            <TableCell align="center">Tag</TableCell>
                                            <TableCell align="center">Action</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {all.map((element, index) => {
                                            const firstDate = element.eventDate && element.eventDate.length > 0 ? new Date(element.eventDate[0].date) : null;
                                            const lastDate = element.eventDate && element.eventDate.length > 1 ? new Date(element.eventDate[element.eventDate.length - 1].date) : null;
                                            {/* let joinedSince;                                           
                                            {element.dateTimes.map((slot, index)=>{
                                            let date = new Date(slot.date);
                                            let monthsArray = ["Jan", "Feb", "March", "Apr", "May", "Jun", "July", "Aug", "Sept", "Oct", "Nov", "Dec",];
                                            joinedSince = `${date.getDate()} ${monthsArray[date.getMonth()]} ${date.getFullYear()}`;})} */}
                                            return (<>
                                                <TableRow key={index + 1}>
                                                    <TableCell align="center">{(parseInt(page) - 1) * limit + index + 1}</TableCell>
                                                    <TableCell align="center">{truncateString(element.title, 10)}</TableCell>
                                                    <TableCell align="center"> {firstDate ?
                                                        element.eventDate.length <= 1
                                                            ? firstDate.toLocaleDateString()
                                                            : firstDate.toLocaleDateString() + " - " + lastDate.toLocaleDateString()
                                                        : "N/A"
                                                    }</TableCell>
                                                    <TableCell align="center">{truncateString(element.place, 30)}</TableCell>
                                                    <TableCell align="center">{element.country}</TableCell>
                                                    <TableCell align="center"><Chip className='statusChipBlock' label={element.event_type}></Chip></TableCell>
                                                    <TableCell align="center">
                                                        {element.event_status == 'Active' ? (
                                                            <Chip
                                                                label={element.event_status}
                                                                className="statusChipActive"
                                                                backgroundColor='green'
                                                            />

                                                        ) : (
                                                            <Chip
                                                                label={element.event_status}
                                                                className="statusChipInActive"
                                                                backgroundColor='orange'
                                                            />

                                                        )}
                                                    </TableCell>
                                                    <TableCell align="center">
                                                        {(() => {
                                                            // NOTE: Handle both array and object formats for backward compatibility
                                                            let tagsArray = [];
                                                            if (element?.tagDetail) {
                                                                if (Array.isArray(element.tagDetail)) {
                                                                    tagsArray = element.tagDetail;
                                                                } else if (element.tagDetail._id) {
                                                                    // NOTE: Handle single object format
                                                                    tagsArray = [element.tagDetail];
                                                                }
                                                            }
                                                            
                                                            return tagsArray.length > 0 ? (
                                                                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '5px', justifyContent: 'center' }}>
                                                                    {tagsArray.map((tag, index) => (
                                                                        <Chip key={tag._id || index} className='statusChipBlock' label={tag.title || 'Unknown Tag'} style={{ margin: '2px' }}></Chip>
                                                                    ))}
                                                                </div>
                                                            ) : (
                                                                <Chip className='statusChipBlock' label="No tag selected yet"></Chip>
                                                            );
                                                        })()}
                                                    </TableCell>
                                                    {/* {element.dateTimes.map((slot)=>{
                                                    <TableCell>{slot.slots.startTime1} - {slot.slots.endTime1}     {slot.slots.startTime2} - {slot.slots.endTime2}
                                                    </TableCell>
                                                    })} */}
                                                    <TableCell align="right">
                                                        <Grid container spacing={1} direction="row" justifyContent="flex-start" alignItems="center">
                                                            <Grid item>
                                                                <Tooltip title="Event Detail" >
                                                                    <NavLink to='/events/detail' state={{ id: element._id, lat: element.location?.coordinates?.[1], lng: element.location?.coordinates?.[0] }} ><RemoveRedEye className='visibilityIc' /></NavLink>
                                                                </Tooltip>
                                                            </Grid>
                                                            <Grid item>
                                                                {element.isDeleted === false ?
                                                                    <Tooltip title="Delete Event" >
                                                                        <RemoveCircleOutline className="userListDelete" onClick={() => { handleRemoveOpen(element._id) }} />
                                                                    </Tooltip>
                                                                    : null}
                                                            </Grid>
                                                        </Grid>
                                                    </TableCell>
                                                </TableRow>
                                            </>
                                            )

                                        })}
                                    </TableBody>
                                </Table>

                            </>
                        }
                    </BackPaper>
                    
                    <div style={{ display: 'flex', flexDirection: "row", justifyContent: "center", alignItems: 'center', marginTop: "20px", marginBottom: "20px" }}>
                        <div>
                            <Pagination count={Math.ceil(eventCount / limit)} page={Math.floor(skip / limit) + 1} variant="outlined" onChange={skipPagination} shape="rounded" />
                        </div>
                    </div>

                    {/* dilogue for delete user */}
                    <Dialog open={openDialogForRemove} aria-labelledby="alert-dialog-title" aria-describedby="alert-dialog-description">
                        <DialogTitle id="alert-dialog-title">
                            {"Are you sure want to remove this event from website?"}
                        </DialogTitle>
                        <DialogActions>
                            <Button onClick={handleRemoveClose}>No</Button>
                            <Button onClick={() => removeEvent(id)} >Yes</Button>
                        </DialogActions>
                    </Dialog>

                    <Dialog open={openDialogForAddEvent} aria-labelledby="alert-dialog-title" aria-describedby="alert-dialog-description">
                        <DialogTitle id="alert-dialog-title">
                            {"What type of event would you like to add?"}
                        </DialogTitle>
                        <DialogActions>
                            <NavLink to='/home/events/normal_events'><Button className='newBtn'>Normal Event</Button></NavLink>
                            <NavLink to='/events/addevent'><Button className='newBtn'>Special Event</Button></NavLink>
                        </DialogActions>
                    </Dialog>

                    <ImportEventDialog open={opened} handleClose={() => setOpened(false)} />
            </BlurredContainer>
            <Backdrop
                sx={{ 
                    color: '#fff', 
                    zIndex: (theme) => theme.zIndex.drawer + 1,
                    backgroundColor: 'rgba(0, 0, 0, 0.4)'
                }}
                open={isLoading}
            >
                <Box sx={{ 
                    display: 'flex', 
                    flexDirection: 'column', 
                    alignItems: 'center', 
                    gap: 2 
                }}>
                    <CircularProgress color="inherit" size={50} thickness={4} />
                    <Typography variant="h6" component="div" sx={{ color: 'white', fontWeight: 500 }}>
                        Loading events...
                    </Typography>
                </Box>
            </Backdrop>
        </>

    )
}

export default Events
